import { Mark, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentTextColour: {
      /** Colours the selected text with an exact `#RRGGBB` value. */
      setTextColour: (colour: string) => ReturnType;
      /** Returns the selected text to the style's automatic colour. */
      unsetTextColour: () => ReturnType;
    };
  }
}

/**
 * Safe palette: dark enough to read on a white page (WCAG AA for body text) and preserved
 * by Word and PDF exports. Any other exact #RRGGBB value can be entered as a custom colour.
 */
export const TEXT_COLOUR_PALETTE = [
  { name: 'Black', value: '#000000' },
  { name: 'Dark grey', value: '#404040' },
  { name: 'Grey', value: '#595959' },
  { name: 'Dark red', value: '#B91C1C' },
  { name: 'Dark orange', value: '#B45309' },
  { name: 'Dark green', value: '#15803D' },
  { name: 'Dark teal', value: '#0F766E' },
  { name: 'Dark blue', value: '#1D4ED8' },
  { name: 'Navy', value: '#1E3A8A' },
  { name: 'Purple', value: '#7E22CE' },
] as const;

const HEX = /^#([0-9a-f]{6})$/i;
const SHORT_HEX = /^#([0-9a-f]{3})$/i;
const RGB = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(?:1|1\.0+)\s*)?\)$/i;

/** Parses one RGB channel (0–255 whole number) or returns null. */
export function parseRgbChannel(value: string): number | null {
  if (!/^\d{1,3}$/.test(value.trim())) return null;
  const channel = Number(value);
  return channel >= 0 && channel <= 255 ? channel : null;
}

export function rgbToHex(rgb: readonly [number, number, number]): string {
  return '#' + rgb.map(channel => channel.toString(16).padStart(2, '0')).join('').toUpperCase();
}

export function hexToRgb(value: string): [number, number, number] | null {
  const match = HEX.exec(value.trim());
  if (!match) return null;
  const hex = match[1]!;
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

/** Normalises `#RGB`, `#RRGGBB` or opaque `rgb()` to `#RRGGBB`; anything else is not a text colour. */
export function normalizeTextColour(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (HEX.test(text)) return text.toUpperCase();
  const short = SHORT_HEX.exec(text);
  if (short) return ('#' + short[1]!.split('').map(digit => digit + digit).join('')).toUpperCase();
  const rgb = RGB.exec(text);
  if (!rgb) return null;
  const channels = [rgb[1]!, rgb[2]!, rgb[3]!].map(parseRgbChannel);
  return channels.every((channel): channel is number => channel !== null) ? rgbToHex([channels[0]!, channels[1]!, channels[2]!]) : null;
}

/**
 * Inline text colour stored as a `textStyle` mark with an exact `#RRGGBB` colour, which the server
 * keeps in saved versions and applies to Word and PDF exports. Pasted colours are normalised;
 * unsupported values are dropped rather than stored.
 */
export const DocumentTextColour = Mark.create({
  name: 'textStyle',
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: element => normalizeTextColour(element.style.color),
        renderHTML: attributes => {
          const colour = normalizeTextColour(attributes.color);
          return colour ? { style: `color: ${colour}`, 'data-text-colour': colour } : {};
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span', getAttrs: element => normalizeTextColour(element.style.color) ? null : false }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setTextColour: colour => ({ chain }) => {
        const normalized = normalizeTextColour(colour);
        return normalized ? chain().setMark(this.name, { color: normalized }).run() : false;
      },
      unsetTextColour: () => ({ chain }) => chain().unsetMark(this.name, { extendEmptyMarkRange: true }).run(),
    };
  },
});
